import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  OnSiteChecklistItem,
  OnSiteChecklistResponse,
  OnSiteChecklistSignature
} from 'src/interfaces/OnSiteChecklistTypes';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import OnSiteChecklistStepRow from './OnSiteChecklistStepRow';
import OnSiteSignatureBlock from './OnSiteSignatureBlock';
import OnSiteImageLightbox from './OnSiteImageLightbox';
import { ON_SITE_IMAGES } from 'src/assets/installation-checklist/sodistore-home';

interface Props {
  installationId: number;
}

type ToastState = {
  open: boolean;
  severity: 'success' | 'error';
  message: string;
};

function OnSiteChecklistTab({ installationId }: Props) {
  const intl = useIntl();
  const { currentUser } = useContext(UserContext);
  const [items, setItems] = useState<OnSiteChecklistItem[]>([]);
  const [signature, setSignature] = useState<OnSiteChecklistSignature | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<ToastState>({
    open: false,
    severity: 'success',
    message: ''
  });
  const [lightbox, setLightbox] = useState<{ open: boolean; src: string | null; alt: string }>({
    open: false,
    src: null,
    alt: ''
  });

  const fetchAll = useCallback(() => {
    setLoading(true);
    axiosConfig
      .get('/GetOnSiteChecklistForInstallation', { params: { installationId } })
      .then((res) => {
        const data = res.data as OnSiteChecklistResponse;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setSignature(data?.signature ?? null);
        setError('');
      })
      .catch(() => setError('Failed to load on-site checklist.'))
      .finally(() => setLoading(false));
  }, [installationId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isSigned = !!signature?.signedAt;
  const isAdmin = currentUser.userType === UserType.admin;
  const disabled = isSigned && !isAdmin;

  const progress = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => i.checked).length;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return { total, done, percent };
  }, [items]);

  const showSaveError = useCallback(() => {
    setToast({
      open: true,
      severity: 'error',
      message: intl.formatMessage({
        id: 'onSiteSaveFailed',
        defaultMessage: 'Änderung konnte nicht gespeichert werden'
      })
    });
  }, [intl]);

  const handleItemUpdate = useCallback(
    async (
      id: number,
      patch: Partial<{ checked: boolean; comments: string; subtasks: string }>
    ) => {
      const params: Record<string, unknown> = { onSiteChecklistItemId: id };
      if (patch.checked !== undefined) params.checked = patch.checked;
      if (patch.comments !== undefined) params.comments = patch.comments;
      if (patch.subtasks !== undefined) params.subtasks = patch.subtasks;
      try {
        const res = await axiosConfig.put('/UpdateOnSiteChecklistItem', null, { params });
        const updated = res.data as OnSiteChecklistItem;
        setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [showSaveError]
  );

  const handleRemarksChange = useCallback(
    async (remarks: string) => {
      try {
        const res = await axiosConfig.put('/UpdateOnSiteChecklistRemarks', null, {
          params: { installationId, remarks }
        });
        setSignature(res.data as OnSiteChecklistSignature);
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [installationId, showSaveError]
  );

  const handleSign = useCallback(
    async (signedByName: string) => {
      try {
        const res = await axiosConfig.post('/SignOnSiteChecklist', null, {
          params: { installationId, signedByName }
        });
        setSignature(res.data as OnSiteChecklistSignature);
        setToast({
          open: true,
          severity: 'success',
          message: intl.formatMessage({
            id: 'onSiteSignedSuccess',
            defaultMessage: 'Checkliste erfolgreich unterzeichnet'
          })
        });
        return true;
      } catch {
        showSaveError();
        return false;
      }
    },
    [installationId, showSaveError, intl]
  );

  const handleResetSignature = useCallback(async () => {
    try {
      const res = await axiosConfig.post('/ResetOnSiteChecklistSignature', null, {
        params: { installationId }
      });
      setSignature(res.data as OnSiteChecklistSignature);
      return true;
    } catch {
      showSaveError();
      return false;
    }
  }, [installationId, showSaveError]);

  const handleImageClick = (filename: string, alt: string) => {
    setLightbox({ open: true, src: ON_SITE_IMAGES[filename] ?? null, alt });
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box mb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h4">
            <FormattedMessage
              id="onSiteChecklistTitle"
              defaultMessage="Vor-Ort-Installations-Checkliste"
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="onSiteChecklistProgress"
              defaultMessage="Fortschritt: {done}/{total} ({percent}%)"
              values={progress}
            />
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress.percent}
          sx={{ height: 8, borderRadius: 4 }}
        />
        {disabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <FormattedMessage
              id="onSiteLocked"
              defaultMessage="Diese Checkliste wurde unterzeichnet und ist gesperrt."
            />
          </Alert>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }}>#</TableCell>
              <TableCell>
                <FormattedMessage id="onSiteStepHeading" defaultMessage="Schritt" />
              </TableCell>
              <TableCell sx={{ width: 260 }}>
                <FormattedMessage id="onSiteImagesHeading" defaultMessage="Bilder" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <OnSiteChecklistStepRow
                key={item.id}
                item={item}
                disabled={disabled}
                onUpdate={handleItemUpdate}
                onImageClick={handleImageClick}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {signature && (
        <OnSiteSignatureBlock
          signature={signature}
          disabled={disabled}
          currentUserType={currentUser.userType}
          onRemarksChange={handleRemarksChange}
          onSign={handleSign}
          onResetSignature={handleResetSignature}
        />
      )}

      <OnSiteImageLightbox
        open={lightbox.open}
        src={lightbox.src}
        alt={lightbox.alt}
        onClose={() => setLightbox({ open: false, src: null, alt: '' })}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default OnSiteChecklistTab;
